import type { EChartsOption } from 'echarts'
import type { DatasetOption } from 'echarts/types/dist/shared'

import { retirementDots } from '../buildMarkPoint'
import type { ChartTheme } from '../chartTheme'
import { withLabelPlacement } from '../milestoneLabelPlacement'
import { wealthRunsMatch } from '../runsMatch'
import type { ChartMilestone, ChartRun, WealthChartSource } from '../types'
import { buildBaseOption } from './base'
import { buildWealthSeries, type RunVisibility } from './series'

export type WealthChartOverlay = {
  /**
   * Baseline source rendered as a second set of series in the same chart.
   * Visibility is toggled via per-series `opacity` (see `previewBaseline`),
   * so both series stay present from ECharts' point of view and the axis
   * auto-fits the union — no manual y-axis math.
   */
  baseline: WealthChartSource
  /**
   * Milestones (e.g. retirement age) drawn on the baseline series. The
   * baseline retirement age can differ from the scenario's, so each side
   * carries its own markLine. Defaults to no milestones when omitted.
   */
  baselineMilestones?: ChartMilestone[]
  /**
   * `true` while the user is pressing the compare button: show baseline,
   * hide scenario. `false` (the default) shows scenario.
   */
  /**
   * Which run, or runs, this chart draws. Defaults to the scenario.
   *
   * `both` draws the plan as it stands behind the scenario — faded on the
   * wealth lines, and as the line along the top of the old stack on the
   * cashflow bars, where a second set of columns would not fit.
   */
  run?: ChartRun
}

const BASELINE_SUFFIX = ' (baseline)'

const SCENARIO_LEGEND_NAMES = ['Net Worth', 'Liquid Assets'] as const
const BASELINE_LEGEND_NAMES = SCENARIO_LEGEND_NAMES.map(
  n => `${n}${BASELINE_SUFFIX}`
)

const stripBaselineSuffix = (name: string): string =>
  name.endsWith(BASELINE_SUFFIX) ? name.slice(0, -BASELINE_SUFFIX.length) : name

const forWealthChart = (m: ChartMilestone): boolean => m.chart !== 'cashflow'

export const buildWealthChartOption = (
  source: WealthChartSource,
  theme: ChartTheme,
  milestones: ChartMilestone[] = [],
  overlay?: WealthChartOverlay
): EChartsOption => {
  const run = overlay?.run ?? 'scenario'
  const ghosts =
    run === 'both' &&
    overlay !== undefined &&
    !wealthRunsMatch(source, overlay.baseline)
  const scenarioVisibility: RunVisibility =
    run === 'baseline' ? 'hidden' : 'lead'
  const baselineVisibility: RunVisibility =
    run === 'baseline' ? 'lead' : ghosts ? 'ghost' : 'hidden'
  const ages = source.map(point => point.age)
  const scenarioSeries = buildWealthSeries(
    theme,
    withLabelPlacement(milestones.filter(forWealthChart), ages),
    scenarioVisibility,
    0,
    '',
    undefined,
    undefined,
    retirementDots(
      source,
      withLabelPlacement(milestones.filter(forWealthChart), ages),
      ghosts ? overlay?.baseline : undefined
    )
  )

  const baselineSeries = overlay
    ? buildWealthSeries(
        theme,
        withLabelPlacement(
          (overlay.baselineMilestones ?? []).filter(forWealthChart),
          overlay.baseline.map(point => point.age)
        ),
        baselineVisibility,
        1,
        BASELINE_SUFFIX,
        undefined,
        undefined,
        retirementDots(
          overlay.baseline,
          withLabelPlacement(
            (overlay.baselineMilestones ?? []).filter(forWealthChart),
            overlay.baseline.map(point => point.age)
          )
        )
      )
    : []

  const dataset: DatasetOption | DatasetOption[] = overlay
    ? [{ source }, { source: overlay.baseline }]
    : { source }

  const base = buildBaseOption(theme, 'Wealth')

  // When overlay is active, filter the legend to just the currently visible
  // series and strip the "(baseline)" suffix so the legend labels stay
  // stable regardless of which set is visible.
  const legend = overlay
    ? {
        ...base.legend,
        data:
          run !== 'baseline'
            ? [...SCENARIO_LEGEND_NAMES]
            : BASELINE_LEGEND_NAMES,
        formatter: stripBaselineSuffix,
      }
    : base.legend

  return {
    ...base,
    legend,
    series: [...scenarioSeries, ...baselineSeries],
    dataset,
  }
}
